import * as React from 'react';
import { Rnd, RndDragCallback } from 'react-rnd';

import { IGNORE_PAGE_CLICK_CLASSNAME, EDIT_PANEL_CLASSNAME } from '../../../containers/Editor/constants';
import { Toggle, Panel, PanelInner, IconMove } from './styles';
import ActionButton from './ActionButton';
import Portal from '../../Portal';
import Utils from '../../../utils';

export enum ActionType {
	EDIT_LINK = 'EDIT_LINK',
	RUN_INDESIGN_ACTION = 'RUN_INDESIGN_ACTION', // e.g. play animation or change MSO state
	TOGGLE_BUTTON_STATE = 'TOGGLE_BUTTON_STATE',
	TOGGLE_VISIBILITY = 'TOGGLE_VISIBILITY',
}

export interface ActionData {
	type: ActionType;
	onClick: (() => void) | Function;
	isInverted?: boolean;
	isDisabled?: boolean;
}

interface ActionsPanelProps {
	id: string;
	actions: ActionData[];
	pageScale: number;
	spreadZoom: number;
	onToggle: () => void;
}

interface Position {
	x: number;
	y: number;
}

const PANEL_WIDTH = 50;
const ACTION_BUTTON_HEIGHT = 38 + 6;
const DRAG_DOTS_HEIGHT = 22 + 6;
const GAP = 8;

const ActionsPanel = ({
	id,
	actions,
	pageScale,
	spreadZoom,
	onToggle,
}: ActionsPanelProps) => {
	const toggleElement = React.useRef<HTMLButtonElement>(null);
	const panelElement = React.useRef<HTMLDivElement>(null);
	const [isOpened, setIsOpened] = React.useState(false);
	const [position, setPosition] = React.useState<Position>({ x: 0, y: 0 });

	const handleToggle = () => {
		setIsOpened(isOpened => !isOpened);
		onToggle();
	};

	const handleDragStop: RndDragCallback = (e, { x, y }) => {
		setPosition({ x, y });
	};

	const updatePosition = () => {
		if (!toggleElement.current) {
			return;
		}

		const panelHeight = actions.length * ACTION_BUTTON_HEIGHT + DRAG_DOTS_HEIGHT;
		const { top, left, right, height } = toggleElement.current.getBoundingClientRect();

		setPosition({
			x: left < (PANEL_WIDTH + GAP)
				? right + GAP
				: left - PANEL_WIDTH - GAP,
			y: top + panelHeight > window.innerHeight
				? top - panelHeight + height
				: top,
		});
	};

	React.useEffect(() => {
		if (!isOpened) {
			return;
		}

		const handleClickOutside = (e: PointerEvent) => {
			const target = e.target as HTMLElement;

			if (
				toggleElement.current &&
				!toggleElement.current.contains(target) &&
				panelElement.current &&
				!panelElement.current.contains(target) &&
				!target.closest(`.${EDIT_PANEL_CLASSNAME}`)
			) {
				setIsOpened(false);
			}
		};

		window.addEventListener('pointerdown', handleClickOutside);

		return () => {
			window.removeEventListener('pointerdown', handleClickOutside);
		};
	}, [isOpened]);

	React.useEffect(() => {
		if (!isOpened) {
			return;
		}

		if (id) {
			updatePosition();
		} else {
			setIsOpened(false);
		}
	}, [id, isOpened]);

	React.useEffect(() => {
		if (!isOpened) {
			return;
		}

		// Standard window 'resize' event isn't triggered in webview on iOS13, it should be listened on window.visualViewport
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', updatePosition);
		} else {
			window.addEventListener('resize', updatePosition);
		}

		return () => {
			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', updatePosition);
			} else {
				window.removeEventListener('resize', updatePosition);
			}
		};
	}, [isOpened]);

	return (
		<>
			<Toggle
				ref={toggleElement}
				pageScale={pageScale}
				spreadZoom={spreadZoom}
				isOpened={isOpened}
				onClick={handleToggle}
			>
				{Utils.getSvgIcon('iconset_plus')}
			</Toggle>
			{isOpened &&
				<Portal parentNode={document.body}>
					<Rnd
						position={position}
						onDragStop={handleDragStop}
						bounds="parent"
						cancel=".prevent-drag"
						enableResizing={false}
					>
						<Panel
							ref={panelElement}
							className={IGNORE_PAGE_CLICK_CLASSNAME}
						>
							<PanelInner className="prevent-drag">
								{actions.map((action) => (
									<ActionButton
										key={action.type}
										{...action}
									/>
								))}
							</PanelInner>
							<IconMove>
								{Utils.getSvgIcon('iconset_6dots')}
							</IconMove>
						</Panel>
					</Rnd>
				</Portal>
			}
		</>
	);
};

export default ActionsPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/ActionsPanel/index.tsx